import React from "react";

import { IPlanDetail } from "./PlanCategorySelectionCardList";
import { MostPopularPlanIcon } from "../../../components/Icons";

interface IPlanCategorySelectionCards {
  planSelected: boolean;
  defaultPlan: boolean;
  planDetail: IPlanDetail;
  onCardClick?: (planValue: string, planType: string) => void;
  planPrice: Record<"annual" | "monthly", number>;
  /** Prequal-denied users can only buy annually (SGPD-587). */
  annualOnly?: boolean;
}

const formatPrice = (value: number) => {
  if (!Number.isFinite(value)) return "$0";
  return `$${Math.round(value)}`;
};

const getDisplayName = (planDetail: IPlanDetail) => planDetail.planName;

const PlanCategorySelectionCards = ({
  planSelected,
  defaultPlan = false,
  planDetail,
  onCardClick,
  planPrice,
  annualOnly = false,
}: IPlanCategorySelectionCards) => {
  const handleOnCardClick = () => {
    onCardClick && onCardClick(planDetail.planValue, planDetail.planType);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOnCardClick();
    }
  };

  const containerClass = planSelected ?
    "border-[1.5px] border-violet-1 bg-violet-9 shadow-[0_4px_12px_rgba(99,122,226,0.15)]" :
    "border border-taupe-50 bg-white hover:border-violet-1";

  const monthlyPrice = planPrice?.monthly ?? 0;
  const annualPrice = planPrice?.annual ?? planDetail.planPrice ?? 0;
  const showMonthly = monthlyPrice > 0 && !annualOnly;

  return (
    <button
      type="button"
      onClick={handleOnCardClick}
      onKeyDown={handleKeyDown}
      aria-pressed={planSelected}
      className={`w-full relative cursor-pointer rounded-xl p-4 transition-all
        flex flex-col items-center text-center gap-1 ${containerClass}`}
    >
      {defaultPlan && (
        <div
          className="absolute flex items-center gap-1 px-2 py-1 rounded-full bg-violet-1
            text-white font-semibold text-[0.625rem] -top-3 left-1/2 -translate-x-1/2"
        >
          <span>Recommended</span>
          <MostPopularPlanIcon />
        </div>
      )}
      {showMonthly ? (
        <>
          <div className="font-semibold text-base">
            {getDisplayName(planDetail)}
          </div>
          <div className="text-base font-semibold leading-tight">
           from {formatPrice(monthlyPrice)}/month
          </div>
          <div className="text-xs text-gray-1">
            {formatPrice(annualPrice)}/year
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center">
          <div className="font-semibold text-base">
            {getDisplayName(planDetail)}
          </div>
          <div className="geograph-medium text-sm leading-5 text-light-black">
            {formatPrice(annualPrice)}/year
          </div>
        </div>
      )}
    </button>
  );
};

export default PlanCategorySelectionCards;
